import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import {
  Estados,
  IAlerta,
  IAlertaMedia,
  IAlertaMensaje,
  ICrearAlertaMensaje,
  ICrearAlertaEstado,
  IDerivarAlerta,
  IDetalleAlerta,
} from 'modelosSeguridad/src';
import { MaterialModule } from '../../../../auxiliares/material.module';
import { ButtonComponent } from '../../../../auxiliares/button/button.component';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { IntegracionesService } from '../../../../auxiliares/servicios/http/integraciones.service';
import { ISirena as ISirenaG } from 'modelos/src';
import type { Map as MapboxMap } from 'mapbox-gl';
import { MapaFacilV2Component } from '../../../../auxiliares/mapbox/mapa-facil-v2.component';
import {
  MapaFacilV2Data,
  MapaFacilV2Service,
} from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { StreetViewComponent } from '../../../../componentes/street-view/street-view.component';
import { CarruselComponent } from '../../../../auxiliares/carrusel/carrusel.component';
import { ImageViewerComponent } from '../../../../auxiliares/image-viewer/image-viewer.component';
import { getUrlFotoVecino } from '../../../../auxiliares/servicios/vecino-foto.helper';
import { OverlayContainer } from '@angular/cdk/overlay';
import {
  AgregarEstadoDialogComponent,
  AgregarEstadoDialogResult,
} from '../agregar-estado-dialog/agregar-estado-dialog.component';
import {
  DerivarAlertaDialogComponent,
  DerivarAlertaDialogResult,
} from '../derivar-alerta-dialog/derivar-alerta-dialog.component';
import { CamarasMultipleComponent } from '../../../standalone/camaras-multiple/camaras-multiple.component';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import {
  coordenadasDeAlerta,
  filtroSirenasCercanas,
  sirenaMasCercanaAAlerta,
} from '../../../../auxiliares/servicios/sirena-de-alerta.helper';

interface DialogData {
  idAlerta: string;
}

@Component({
  selector: 'app-tratar-alerta-seguridad',
  templateUrl: './tratar-alerta-seguridad.component.html',
  styleUrl: './tratar-alerta-seguridad.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatTooltipModule,
    ButtonComponent,
    MapaFacilV2Component,
    StreetViewComponent,
    CarruselComponent,
    ImageViewerComponent,
  ],
})
export class TratarAlertaSeguridadComponent implements OnInit, OnDestroy {
  data = inject<DialogData>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<MatDialogRef<TratarAlertaSeguridadComponent>>(MatDialogRef);
  private service = inject(IntegracionesService);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private cdk = inject(OverlayContainer);
  private el = inject(ElementRef);
  private mapaFacilV2Service = inject(MapaFacilV2Service);
  private lista2 = inject(Lista2Service);
  private map: MapboxMap | null = null;

  public loading = true;
  public dragBoundary: HTMLElement | null = null;
  public actualizando = false;
  public detalle?: IDetalleAlerta;
  public title = 'Atención de Alerta';
  public huboCambios = false;
  public alertaCoords: [number, number] | null = null;
  public descargandoAudio = false;

  //Sirena reconstruida por cercania a la alerta.

  public sirenaVinculada?: ISirenaG;
  public distanciaSirena?: number;

  private static readonly REFRESH_INTERVAL_MS = 15_000;
  private refreshInterval?: ReturnType<typeof setInterval>;
  private refrescando = false;
  private ubicacionesEnMapa = 0;

  private static readonly SHOW_STREET_VIEW_KEY = 'tratarAlerta.showStreetView';
  public showStreetView = signal(
    localStorage.getItem(
      TratarAlertaSeguridadComponent.SHOW_STREET_VIEW_KEY,
    ) !== 'false',
  );

  constructor() {
    effect(() => {
      const visible = this.showStreetView();
      localStorage.setItem(
        TratarAlertaSeguridadComponent.SHOW_STREET_VIEW_KEY,
        String(visible),
      );
      // El pane del mapa cambia de ancho cuando aparece/desaparece Street View;
      // mapbox-gl requiere resize() manual para repintar al nuevo tamaño.
      setTimeout(() => this.map?.resize(), 200);
    });
  }

  public enviandoMensaje = false;
  public alertaMensajes: IAlertaMensaje[] = [];
  private configSeguridad: {
    verSucesos: boolean;
    categoriasDeEvento: string[];
    tagsDeEvento: string[];
  } = { verSucesos: false, categoriasDeEvento: [], tagsDeEvento: [] };

  get estadosHistorial() {
    return [...(this.detalle?.estados ?? [])].reverse();
  }

  get mostrarCamposVecino(): boolean {
    if (!this.alerta) return false;
    if (this.alerta.idOperador) return false;
    if (this.alerta.idCentroDerivado && this.alerta.creadoPorMonitoreo)
      return false;
    return true;
  }

  get estadosDisponibles(): Estados[] {
    if (this.estadoActual === 'Móvil Enviado') {
      return ['En Proceso', 'Móvil En Destino', 'Finalizada'];
    }
    if (this.estadoActual === 'Móvil En Destino') {
      return ['En Proceso', 'Finalizada'];
    }
    return ['En Proceso', 'Móvil Enviado', 'Finalizada'];
  }

  public mensajeForm = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.maxLength(300)],
  });

  get alerta(): IAlerta | undefined {
    return this.detalle?.alerta;
  }

  get estadoActual(): Estados | undefined {
    return this.alerta?.estadoActual;
  }

  get tratamientoIniciado(): boolean {
    const estado = this.estadoActual;
    if (!estado) return false;
    return !['Nueva', 'Vista', 'Sin Tratamiento'].includes(estado);
  }

  get esFinalizada(): boolean {
    return (
      this.estadoActual === 'Finalizada' ||
      this.estadoActual === 'Derivada Finalizada' ||
      this.estadoActual === 'Derivada Compartida Finalizada' ||
      this.estadoActual === 'Derivada Externa Finalizada'
    );
  }

  get puedeDevolver(): boolean {
    return (
      !!this.alerta?.idCentroDerivado ||
      this.alerta?.tipoDelegacion === 'externa'
    );
  }

  get puedeDerivar(): boolean {
    return this.helper.tienePermiso('Alertas Seguridad - Derivar');
  }

  public mostrarVisorFotoVecino = false;

  get sirenaTieneCamaras(): boolean {
    return !!this.sirenaVinculada?.idsAsignados?.length;
  }

  get distanciaSirenaTexto(): string {
    if (this.distanciaSirena === undefined) return '';
    return `${Math.round(this.distanciaSirena)} m`;
  }

  get fotoVecino(): string | undefined {
    return getUrlFotoVecino(this.alerta?.configVecino);
  }

  get vecinoNombre(): string {
    return (
      this.alerta?.configVecino?.datosPersonales?.nombre ||
      this.alerta?.vecino?.nombre ||
      'Anónimo'
    );
  }

  get telefono(): string {
    return (
      this.alerta?.configVecino?.datosPersonales?.telefono ||
      this.alerta?.vecino?.telefono ||
      ''
    );
  }

  get dni(): string {
    return (
      this.alerta?.configVecino?.datosPersonales?.dni ||
      this.alerta?.vecino?.dni ||
      ''
    );
  }

  get sexo(): string {
    const sexo =
      this.alerta?.configVecino?.datosPersonales?.sexo ??
      this.alerta?.vecino?.sexo;
    if (sexo === true) return 'Masculino';
    if (sexo === false) return 'Femenino';
    return '';
  }

  get edad(): string {
    const fechaNacimiento =
      this.alerta?.configVecino?.datosPersonales?.fechaNacimiento ||
      this.alerta?.vecino?.fechaNacimiento;
    if (!fechaNacimiento) return '';

    const nacimiento = new Date(fechaNacimiento).getTime();
    if (Number.isNaN(nacimiento)) return '';

    const hoy = Date.now();
    const years = Math.floor((hoy - nacimiento) / (1000 * 60 * 60 * 24 * 365));
    return `${years} años`;
  }

  private obtenerMimeDesdeUrl(url: string): string | undefined {
    try {
      const limpia = url.split('?')[0].toLowerCase();
      if (limpia.endsWith('.jpg') || limpia.endsWith('.jpeg'))
        return 'image/jpeg';
      if (limpia.endsWith('.png')) return 'image/png';
      if (limpia.endsWith('.webp')) return 'image/webp';
      if (limpia.endsWith('.gif')) return 'image/gif';
      if (limpia.endsWith('.bmp')) return 'image/bmp';
      if (limpia.endsWith('.svg')) return 'image/svg+xml';
      return undefined;
    } catch {
      return undefined;
    }
  }

  public urlsDeEstado(estado: unknown): string[] {
    const e = estado as { multimedia?: IAlertaMedia[] };
    return (e?.multimedia || [])
      .map((m) => m?.url)
      .filter((u): u is string => !!u);
  }

  private esImagen(m: IAlertaMedia): boolean {
    return (m.mime?.startsWith('image/') ?? false) || m.tipo === 'imagen';
  }

  private esAudio(m: IAlertaMedia): boolean {
    return (m.mime?.startsWith('audio/') ?? false) || m.tipo === 'audio';
  }

  /** Imágenes del multimedia a nivel alerta (para el carrusel). */
  public get imagenesAlerta(): string[] {
    return (this.detalle?.multimedia || [])
      .filter((m) => m?.url && this.esImagen(m))
      .map((m) => m.url as string);
  }

  /** Chunks de audio de la alerta, ordenados cronológicamente. */
  public get audiosAlerta(): IAlertaMedia[] {
    return (this.detalle?.multimedia || [])
      .filter((m) => m?.url && !this.esImagen(m) && this.esAudio(m))
      .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''));
  }

  /** Multimedia a nivel alerta que no es imagen ni audio (video/otros). */
  public get mediaOtrosAlerta(): IAlertaMedia[] {
    return (this.detalle?.multimedia || []).filter(
      (m) => m?.url && !this.esImagen(m) && !this.esAudio(m),
    );
  }

  public get hayMultimedia(): boolean {
    return !!(
      this.imagenesAlerta.length ||
      this.audiosAlerta.length ||
      this.mediaOtrosAlerta.length
    );
  }

  public async descargarAudioCompleto(): Promise<void> {
    if (this.descargandoAudio || !this.data.idAlerta) return;
    this.descargandoAudio = true;
    try {
      await this.service.exportarAudioAlerta(this.data.idAlerta);
    } finally {
      this.descargandoAudio = false;
    }
  }

  public formatTiempo(ms?: number | null): string {
    if (!ms) return '';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([
      this.cargar(),
      this.service
        .getClienteSeguridad()
        .then((c) => {
          this.configSeguridad = c;
        })
        .catch(() => {}),
    ]);
    this.setupDragBoundary();
    this.iniciarAutoRefresh();
  }

  ngOnDestroy(): void {
    this.detenerAutoRefresh();
  }

  // Mientras la alerta está activa llegan chunks de audio, mensajes y
  // ubicaciones nuevas; se refresca en silencio para no perder nada.
  private iniciarAutoRefresh(): void {
    if (this.esFinalizada || this.refreshInterval) return;
    this.refreshInterval = setInterval(() => {
      void this.refrescarSilencioso();
    }, TratarAlertaSeguridadComponent.REFRESH_INTERVAL_MS);
  }

  private detenerAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  }

  private async refrescarSilencioso(): Promise<void> {
    if (this.loading || this.actualizando || this.refrescando) return;
    this.refrescando = true;
    try {
      this.detalle = await this.service.getAlertaById(this.data.idAlerta);
      // Solo re-alimentar el mapa si hay ubicaciones nuevas, sin recentrar
      // para no pisar el paneo del operador.
      if ((this.detalle?.ubicaciones?.length || 0) !== this.ubicacionesEnMapa) {
        this.actualizarMapa(false);
      }
      await this.cargarMensajes();
      if (this.esFinalizada) this.detenerAutoRefresh();
    } catch (error) {
      console.error('Error refrescando alerta', error);
    } finally {
      this.refrescando = false;
    }
  }

  private setupDragBoundary(): void {
    const routerElement = document.getElementById('router');

    if (routerElement) {
      this.dragBoundary = routerElement;
    } else {
      const overlayContainer = this.cdk.getContainerElement();
      if (overlayContainer) {
        this.dragBoundary = overlayContainer;
      } else {
        this.dragBoundary = document.body;
      }
    }
  }

  public cerrar(): void {
    this.dialogRef.close(this.huboCambios);
  }

  public async iniciarTratamiento(): Promise<void> {
    if (!this.alerta?._id) return;
    this.actualizando = true;
    try {
      const body: ICrearAlertaEstado = {
        idAlerta: this.alerta._id,
        idCliente: this.alerta.idCliente,
        estado: 'Pendiente',
        notaInterna: 'Tratamiento iniciado desde Gestión',
      };
      await this.service.cambiarEstadoAlerta(body);
      this.helper.notifSuccess('Tratamiento iniciado');
      this.huboCambios = true;
      await this.cargar();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.actualizando = false;
    }
  }

  public abrirAgregarEstado(): void {
    if (!this.alerta?._id) return;
    const ref = this.formularios.abrir(AgregarEstadoDialogComponent, {
      tamano: 'mediano',
      data: {
        estadosDisponibles: this.estadosDisponibles,
        mostrarCamposVecino: this.mostrarCamposVecino,
        verSucesos: this.configSeguridad.verSucesos,
        categorias: this.configSeguridad.categoriasDeEvento,
        tags: this.configSeguridad.tagsDeEvento,
      },
    });
    ref
      .afterClosed()
      .subscribe((res: AgregarEstadoDialogResult | null | undefined) => {
        if (res) void this.guardarEstado(res);
      });
  }

  public abrirDerivar(): void {
    if (!this.alerta?._id) return;
    const ref = this.formularios.abrir(DerivarAlertaDialogComponent, {
      tamano: 'chico',
      data: {},
    });
    ref
      .afterClosed()
      .subscribe((res: DerivarAlertaDialogResult | null | undefined) => {
        if (res) void this.derivar(res);
      });
  }

  private async guardarEstado(res: AgregarEstadoDialogResult): Promise<void> {
    if (!this.alerta?._id) return;
    this.actualizando = true;
    try {
      const multimedia: IAlertaMedia[] | undefined = res.urls?.length
        ? res.urls.map((url) => ({
            tipo: 'imagen',
            url,
            mime: this.obtenerMimeDesdeUrl(url),
            fecha: new Date().toISOString(),
          }))
        : undefined;
      const body: ICrearAlertaEstado & { multimedia?: IAlertaMedia[] } = {
        idAlerta: this.alerta._id,
        idCliente: this.alerta.idCliente,
        estado: res.estado,
        notaPredefinida: res.notaPredefinida,
        nota: res.nota,
        notaInterna: res.notaInterna,
        causante: res.causante,
        multimedia,
      };
      const query: { categoria?: string; tags?: string } = {};
      if (res.categoriasDeEvento) query.categoria = res.categoriasDeEvento;
      if (res.tagsDeEvento?.length) query.tags = res.tagsDeEvento.join(',');
      await this.service.cambiarEstadoAlerta(
        body,
        Object.keys(query).length ? query : undefined,
      );
      this.helper.notifSuccess('Estado agregado correctamente');
      this.huboCambios = true;
      await this.cargar();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.actualizando = false;
    }
  }

  public async liberar(): Promise<void> {
    if (!this.alerta?._id) return;
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      '¿Liberar la alerta? Podrá ser tomada por otro operador.',
    );
    if (!ok) return;

    this.actualizando = true;
    try {
      const body: ICrearAlertaEstado = {
        idAlerta: this.alerta._id,
        idCliente: this.alerta.idCliente,
        estado: 'Liberada',
      };
      await this.service.cambiarEstadoAlerta(body);
      this.helper.notifSuccess('Alerta liberada');
      this.huboCambios = true;
      await this.cargar();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.actualizando = false;
    }
  }

  private async derivar(res: DerivarAlertaDialogResult): Promise<void> {
    if (!this.alerta?._id) return;
    this.actualizando = true;
    try {
      const body: IDerivarAlerta = {
        tipo: res.tipo,
        idCentroMonitoreo: res.idCentroMonitoreo,
        entidadExterna: res.entidadExterna,
      };
      await this.service.derivarAlerta(this.alerta._id, body);
      this.helper.notifSuccess('Alerta derivada correctamente');
      this.huboCambios = true;
      await this.cargar();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.actualizando = false;
    }
  }

  public async devolver(): Promise<void> {
    if (!this.alerta?._id) return;
    const ok = await this.dialogService.confirm(
      'Confirmá la acción',
      '¿Devolver la alerta a origen? No podrás seguir tratándola desde este centro.',
    );
    if (!ok) return;

    this.actualizando = true;
    try {
      await this.service.devolverAlerta(this.alerta._id);
      this.helper.notifSuccess('Alerta devuelta correctamente');
      this.huboCambios = true;
      await this.cargar();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.actualizando = false;
    }
  }

  public async enviarMensaje(): Promise<void> {
    const alerta = this.alerta;
    const mensaje = this.mensajeForm.value?.trim();
    if (!alerta?._id || !mensaje) return;

    this.enviandoMensaje = true;
    try {
      const body: ICrearAlertaMensaje = {
        idAlerta: alerta._id,
        idCliente: alerta.idCliente,
        mensaje,
      };
      await this.service.crearAlertaMensaje(body);
      this.mensajeForm.setValue('');
      await this.cargarMensajes();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.enviandoMensaje = false;
    }
  }

  private coordsDe(dato?: {
    geojson?: { coordinates?: number[] };
    ubicacion?: { lat?: number; lng?: number };
  }): [number, number] | null {
    const geo = dato?.geojson?.coordinates;
    if (geo?.length === 2) return [geo[0], geo[1]];
    const ubi = dato?.ubicacion;
    if (typeof ubi?.lng === 'number' && typeof ubi?.lat === 'number') {
      return [ubi.lng, ubi.lat];
    }
    return null;
  }

  private actualizarMapa(recentrar = true): void {
    this.alertaCoords = this.coordsDe(this.alerta);
    this.feedMapData(recentrar);
  }

  public onMapaListo(map: MapboxMap): void {
    this.map = map;
    this.feedMapData(true);
  }

  /** Recorrido del vecino durante la alerta, cronológico y sin repetidos consecutivos. */
  private puntosRecorrido(): [number, number][] {
    const ubicaciones = [...(this.detalle?.ubicaciones || [])].sort((a, b) =>
      (a.fecha || '').localeCompare(b.fecha || ''),
    );
    const puntos: [number, number][] = [];
    for (const u of ubicaciones) {
      const coords = this.coordsDe(u);
      if (!coords) continue;
      const anterior = puntos[puntos.length - 1];
      if (anterior && anterior[0] === coords[0] && anterior[1] === coords[1]) {
        continue;
      }
      puntos.push(coords);
    }
    return puntos;
  }

  private feedMapData(recentrar: boolean): void {
    if (!this.map || !this.alertaCoords) {
      return;
    }

    // Limpiar singletons para que datos de este mapa pequeño no contaminen
    // otros mapas (mismo patrón que map-card.component).
    this.mapaFacilV2Service.clearAllData();
    this.mapaFacilV2Service.clearCache();

    const recorrido = this.puntosRecorrido();
    this.ubicacionesEnMapa = this.detalle?.ubicaciones?.length || 0;

    const puntos: { _id: string; coordenadas: [number, number] }[] = [
      { _id: this.data.idAlerta, coordenadas: this.alertaCoords },
    ];
    const ultima = recorrido[recorrido.length - 1];
    if (
      ultima &&
      (ultima[0] !== this.alertaCoords[0] || ultima[1] !== this.alertaCoords[1])
    ) {
      puntos.push({
        _id: `${this.data.idAlerta}-ultima-ubicacion`,
        coordenadas: ultima,
      });
    }

    const v2Data: MapaFacilV2Data = { puntos };
    if (recorrido.length >= 2) {
      v2Data.polylines = [
        {
          id: 'recorrido-alerta',
          points: recorrido,
          width: 3,
          dash: true,
          centrar: recentrar,
        },
      ];
    } else if (recentrar) {
      v2Data.center = this.alertaCoords;
      v2Data.zoom = 16;
    }
    this.mapaFacilV2Service.updateData(v2Data);

    setTimeout(() => this.map?.resize(), 100);
  }

  private async cargar(): Promise<void> {
    this.loading = true;
    try {
      this.detalle = await this.service.getAlertaById(this.data.idAlerta);
      this.actualizarMapa();
      await Promise.all([this.cargarMensajes(), this.resolverSirenaDeAlerta()]);
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  private async cargarMensajes(): Promise<void> {
    const idAlerta = this.alerta?._id;
    if (!idAlerta) {
      this.alertaMensajes = [];
      return;
    }

    try {
      const res = await this.service.getAlertaMensajes({
        filter: JSON.stringify({ idAlerta }),
        sort: 'fecha',
      });
      this.alertaMensajes = res?.datos || [];
    } catch (error) {
      console.error('Error cargando mensajes de alerta', error);
      this.alertaMensajes = [];
    }
  }

  /**
   * Reconstruye por cercania la sirena que sono con esta alerta y deja a mano
   * sus camaras. Dos pasos, como en seguridad: prefiltro geografico en Mongo
   * y la medicion contra el radio de accion en memoria.
   */
  private async resolverSirenaDeAlerta(): Promise<void> {
    this.sirenaVinculada = undefined;
    this.distanciaSirena = undefined;

    const puntoAlerta = coordenadasDeAlerta(this.alerta);
    if (!puntoAlerta) return;
    // Sin el modulo de Sirenas el endpoint /sirenas responde 403.
    if (!this.helper.tieneModulo('Sirenas')) return;

    try {
      const res = await firstValueFrom(
        this.lista2.sirenasG({
          filter: JSON.stringify(filtroSirenasCercanas(puntoAlerta)),
          select: '_id chipId direccion ubicacion idsAsignados activa',
          unlimited: true,
          limit: 0,
          excludeTotalCount: true,
        }),
      );
      const resuelta = sirenaMasCercanaAAlerta(this.alerta, res?.datos || []);
      this.sirenaVinculada = resuelta?.sirena;
      this.distanciaSirena = resuelta?.distancia;
    } catch (error) {
      // Que falle la reconstruccion de la sirena no puede tumbar la atencion.
      console.error('Error resolviendo la sirena de la alerta', error);
    }
  }

  /**
   * Abre las camaras vinculadas a la sirena. Reusa el mismo visor y la misma
   * disposicion que el tratamiento de eventos genericos: dialog sin backdrop
   * arriba a la derecha para que no quede tapado por la atencion.
   */
  public verCamaras(): void {
    const idsCamaras = this.sirenaVinculada?.idsAsignados;
    if (!idsCamaras?.length) return;

    const nombreEntidad = `Sirena ${this.sirenaVinculada?.chipId || ''}`.trim();
    const abierto = this.dialog.openDialogs.find(
      (d) =>
        d.componentInstance instanceof CamarasMultipleComponent &&
        d.componentInstance.nombreEntidad === nombreEntidad,
    );
    if (abierto) {
      (abierto.componentInstance as CamarasMultipleComponent).bringToFront();
      return;
    }

    const ref = CamarasMultipleComponent.open(
      this.dialog,
      {
        data: idsCamaras,
        nombreEntidad,
        fechaEvento: this.alerta?.fechaCreacion,
      },
      {
        hasBackdrop: false,
        closeOnNavigation: false,
        disableClose: true,
        position: { top: '12px', right: '12px' },
      },
    );
    ref.afterOpened().subscribe(() => ref.componentInstance?.bringToFront());
  }

  public bringToFront(): void {
    if (!this.dragBoundary) {
      this.setupDragBoundary();
    }

    const dialogsContainer = Array.from(
      this.cdk.getContainerElement().childNodes,
    );
    const dialogContainerTagName = 'MAT-DIALOG-CONTAINER';
    const parentEl = this.el.nativeElement.closest(dialogContainerTagName);
    const dialogId = parentEl?.getAttribute('id');
    const previousDialog = this.cdk.getContainerElement().lastElementChild;
    const focusedDialog = (dialogsContainer as HTMLElement[]).find(
      (el: HTMLElement) => {
        return el.querySelector(`#${dialogId}`);
      },
    );
    if (focusedDialog !== previousDialog) {
      if (focusedDialog) {
        this.cdk.getContainerElement().appendChild(focusedDialog);
      } else {
        console.error('Dialog not found with id:', dialogId);
      }
    }
  }
}
